import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

import coverImage from "@/assets/blog/fidelisation-emotionnelle-vs-transactionnelle/couverture.webp";
import erosionImage from "@/assets/blog/fidelisation-emotionnelle-vs-transactionnelle/erosion-programme-fidelite-transactionnel.webp";
import leviersImage from "@/assets/blog/fidelisation-emotionnelle-vs-transactionnelle/leviers-fidelisation-emotionnelle.webp";

const FidelisationEmotionnelleVsTransactionnelle = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Fidélisation émotionnelle vs fidélisation transactionnelle : laquelle crée une vraie loyauté ?",
      seoConfig.blogFidelisationEmotionnelle.description,
      "/fidelisation-emotionnelle-vs-transactionnelle"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Fidélisation émotionnelle vs transactionnelle", path: "/fidelisation-emotionnelle-vs-transactionnelle" }
    ]),
    generateArticleSchema(
      "Fidélisation émotionnelle vs fidélisation transactionnelle : laquelle crée une vraie loyauté ?",
      seoConfig.blogFidelisationEmotionnelle.description,
      "https://unotif.com/fidelisation-emotionnelle-vs-transactionnelle",
      "2026-04-11"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogFidelisationEmotionnelle} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Fidélisation émotionnelle vs transactionnelle</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Fidélisation émotionnelle vs fidélisation transactionnelle : laquelle crée une vraie loyauté ?
            </h1>

            {/* Cover Image */}
            <img
              src={coverImage}
              alt="Comparaison entre fidélisation émotionnelle et fidélisation transactionnelle avec impact sur la loyauté client"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un programme de fidélité avec des points, des tampons et des remises : c'est de la <strong className="font-semibold">fidélisation transactionnelle</strong>. Le client revient pour la récompense. Retirez la récompense, il part.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client qui revient parce qu'il fait confiance, parce qu'il se sent reconnu, parce qu'il partage les valeurs de votre commerce : c'est de la <strong className="font-semibold">fidélisation émotionnelle</strong>. Ce client reste même quand un concurrent propose moins cher. Comprendre la différence entre ces deux approches, c'est comprendre pourquoi certains programmes de fidélité coûteux ne retiennent personne, et pourquoi certains petits commerces gardent leurs clients pendant des années sans aucun système formel. Pour une vision complète des stratégies de fidélisation, notre guide sur <Link to="/fidelisation-client-guide" className="text-[#0c97a9] hover:underline">comment fidéliser ses clients</Link> pose les bases avant d'entrer dans ce débat.
            </p>

            {/* Section 1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              La fidélisation transactionnelle : rapide à mettre en place, facile à perdre
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">fidélisation transactionnelle</strong> repose sur un principe simple : le client est récompensé pour ses achats. Points, tampons, remises, cashback, accès à des offres préférentielles. Le mécanisme est clair, mesurable, et produit des résultats visibles sur la fréquence de visite à court terme.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ses forces sont réelles. Elle est facile à comprendre pour le client, rapide à mettre en place, et elle génère des retours mesurables dès les premières semaines. Pour un commerce qui n'a jamais structuré sa <strong className="font-semibold">fidélité client</strong>, c'est souvent le point d'entrée le plus naturel.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ses limites sont tout aussi réelles. La fidélité dure aussi longtemps que la récompense dure. Si un concurrent offre une remise plus attractive ou un programme plus généreux, une partie de votre base migre vers lui sans état d'âme. C'est une fidélité achetée, pas construite.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              58 % des membres de programmes transactionnels les abandonnent dans les 6 mois suivant leur inscription. Non pas parce que le programme est mauvais, mais parce qu'il ne crée pas de raison de rester au-delà de la mécanique de points. Le <strong className="font-semibold">programme de fidélité transactionnel</strong> crée des habitués, pas des fans.
            </p>

            {/* Image: erosion */}
            <img
              src={erosionImage}
              alt="Schéma en entonnoir inversé montrant la perte d'engagement dans un programme de fidélité transactionnel"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              La fidélisation émotionnelle : plus longue à construire, beaucoup plus durable
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">fidélisation émotionnelle</strong> fonctionne différemment. Le client reste parce qu'il se sent reconnu, valorisé, parce qu'il s'identifie aux valeurs de votre marque ou de votre commerce. La récompense n'est pas absente, mais elle n'est pas le moteur principal.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Trois piliers structurent cet attachement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">reconnaissance individuelle</strong> est le premier. Un client qui se sent vu, traité comme une personne et non comme un numéro de compte, développe un lien que les remises ne peuvent pas racheter. Utiliser son prénom, se souvenir de sa commande habituelle, faire référence à son historique de visite : ces gestes simples construisent quelque chose que la concurrence ne copie pas facilement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">confiance construite dans la durée</strong> est le deuxième. Un commerçant qui livre ce qu'il promet, au moment promis, de façon cohérente sur le long terme, crée plus d'attachement que dix remises. La confiance est le substrat invisible sur lequel repose toute relation durable. Elle ne se construit pas en une interaction ; elle se consolide sur des dizaines d'expériences cohérentes.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">sentiment d'appartenance</strong> est le troisième. Un client qui se sent membre d'une communauté, d'un cercle restreint, est fondamentalement différent d'un client qui cumule des points. Il ne consomme pas : il appartient. Et ce qu'on appartient, on ne le quitte pas pour 10 % de remise chez un concurrent.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Apple fidélise sans programme de points. Le café de quartier dont le gérant connaît votre prénom et votre commande habituelle fidélise sans budget marketing. Le coach dont les clients reviennent 3 ans après une formation fidélise sans mécanique transactionnelle. La <strong className="font-semibold">fidélisation émotionnelle</strong> crée des ambassadeurs, pas seulement des acheteurs récurrents.
            </p>

            {/* Section 3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Pourquoi la plupart des programmes de points ne créent pas de vraie fidélité
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le paradoxe est là : un programme de points bien exécuté peut fonctionner techniquement tout en échouant à créer une vraie <strong className="font-semibold">loyauté client</strong>. Voici pourquoi.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un programme de points répond à la question "comment inciter mon client à revenir ?" Il ne répond pas à la question "pourquoi mon client veut revenir ?" Ce sont deux questions radicalement différentes, et seule la deuxième construit quelque chose de durable.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le profil du "deal-seeker" illustre ce problème. Ce client s'inscrit au programme, cumule des points rapidement, utilise ses récompenses, et disparaît jusqu'au prochain cycle d'offres intéressantes. Il n'est pas fidèle : il est opportuniste. Sa <strong className="font-semibold">valeur vie client</strong> (CLV) est structurellement inférieure à celle d'un client motivé par l'attachement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La récompense, à force d'être attendue, perd son pouvoir émotionnel. Un client qui reçoit systématiquement ses points ne les ressent plus comme un geste : c'est devenu une mécanique automatique. La surprise, l'attention inattendue, l'avantage non planifié : ces éléments gardent leur puissance précisément parce qu'ils ne sont pas prévisibles.
            </p>

            {/* Section 4 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les 5 leviers concrets de la fidélisation émotionnelle
            </h2>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              1. La reconnaissance nominale et comportementale
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La reconnaissance commence par le prénom. Elle se renforce quand le client perçoit que vous connaissez ses habitudes : sa commande habituelle, sa fréquence de visite, ses préférences. Un <strong className="font-semibold">CRM</strong> sophistiqué n'est pas nécessaire pour démarrer. Une carte wallet avec le prénom du client affiché et un historique de points crée déjà un sentiment de personnalisation que beaucoup de grandes structures n'atteignent pas.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              2. La cohérence de la promesse
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La fidélité émotionnelle naît de la confiance, et la confiance naît de la cohérence. Un commerçant qui dit "je livre en 24 heures" et tient cette promesse cinquante fois de suite construit quelque chose d'inattaquable. Il ne s'agit pas de faire des gestes extraordinaires : il s'agit de faire des gestes ordinaires de façon extraordinairement régulière.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              3. La communication à valeur ajoutée
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le client qui reçoit uniquement des offres commerciales développe une relation transactionnelle avec votre marque. Il attend les promotions, clique dessus, et ignore le reste. Le client qui reçoit du contenu utile, des conseils, des coulisses de votre activité développe de la confiance. Et la confiance précède l'achat, pas l'inverse.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              4. Le sentiment d'exclusivité
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Faire sentir à votre client qu'il fait partie d'un cercle restreint est l'un des leviers émotionnels les plus puissants. Vente privée membres, accès anticipé aux nouveautés, contenu réservé aux abonnés actifs : ces formats créent un sentiment d'appartenance qui se traduit en fidélité durable. La <strong className="font-semibold">notification push wallet</strong> est un canal idéal pour ce type de message : il arrive directement sur le téléphone, comme un message personnel, et renforce le sentiment d'être traité différemment.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              5. La récupération de l'erreur
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La façon dont une entreprise gère ses erreurs détermine l'attachement bien plus que la qualité habituelle. Un problème bien géré, résolu rapidement, avec une attention sincère, crée paradoxalement plus de fidélité émotionnelle qu'un parcours sans incident. C'est ce qu'on appelle le paradoxe de la récupération de service : le client qui a vécu une erreur bien gérée est souvent plus attaché que celui qui n'a jamais eu de problème.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Les clients émotionnellement fidèles sont les premiers à devenir ambassadeurs. Notre guide sur comment transformer un client satisfait en client ambassadeur explique comment créer les conditions de ce passage.
            </p>

            {/* Image: leviers */}
            <img
              src={leviersImage}
              alt="Infographie des 5 leviers de la fidélisation émotionnelle avec reconnaissance client, communication et expérience"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 5 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Faut-il choisir entre les deux approches, ou les combiner ?
            </h2>

            {/* Tableau comparatif */}
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Critère</th>
                    <th className="px-4 py-3 text-left font-semibold">Transactionnelle</th>
                    <th className="px-4 py-3 text-left font-semibold">Émotionnelle</th>
                    <th className="px-4 py-3 text-left font-semibold">Combinée</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Vitesse de mise en place</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Rapide (1 semaine)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Progressive (3 à 6 mois)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Progressive</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Coût</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Moyen (récompenses)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible à moyen</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Optimisé</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Durabilité de la fidélité</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible à moyenne</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Élevée</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Très élevée</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Résistance concurrente</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Élevée</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Élevée</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Profil idéal</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Commerce fréquent, panier faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Service, coaching, B2B</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Tous profils</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La réponse est presque toujours : les deux. Le <strong className="font-semibold">programme transactionnel</strong> crée le prétexte au contact et la mécanique de retour. L'approche émotionnelle crée l'attachement qui rend le programme indispensable au-delà des récompenses.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">wallet marketing</strong> réunit les deux depuis une seule interface : il permet d'envoyer des notifications personnalisées (dimension émotionnelle) tout en gérant un système de points ou de tampons numériques (dimension transactionnelle). Le client voit ses points progresser sur sa carte, reçoit des messages adaptés à son profil, et accède à des avantages réservés aux membres. Les deux logiques se renforcent au lieu de se concurrencer.
            </p>

            {/* Conclusion */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Conclusion
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les programmes transactionnels attirent. La <strong className="font-semibold">fidélisation émotionnelle</strong> retient. Les deux réunis créent des ambassadeurs. Construire l'aspect émotionnel ne demande pas de budget supplémentaire : il demande de l'attention, de la cohérence et de la régularité. Un message personnalisé au bon moment vaut plus qu'une remise générique de 15 %. Unotif permet d'associer programme de points et communications personnalisées depuis une seule interface.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FidelisationEmotionnelleVsTransactionnelle;
